import { useCallback, useEffect, useRef, useState } from "react";
import { dashboardAPI } from "../Api/dashboardAPI";
import { ProgressStatus } from "../components/reusable/ProgressBar/ProgressBar";
import {
  EstadoTablasMirrorResponse,
  ProgresoSyncTablaMirror,
  SyncMode,
  SyncPhase,
} from "../interfaces/dashboardTypes";

export interface SyncSnapshot {
  tabla: string;
  status: ProgressStatus;
  /** null mientras el avance es indeterminado (preparando o upsert-hash). */
  percent: number | null;
  elapsedMs: number;
  startedAt: number;
  cantidadRegistros: number;
  cantidadRegistrosOracle: number;
  syncMode: SyncMode;
  syncPhase?: SyncPhase | null;
  syncError?: string | null;
  label?: string;
}

export interface RunSyncOptions {
  /**
   * Valor de ultima_sincronizacion previo al disparo, devuelto por dispararSyncTablaMirror.
   * Se omite al reanudar un run ya arrancado, porque no hay contra qué comparar.
   */
  marcadorSincronizacion?: string | null;
  usarUpsertHash?: boolean;
  /** Tiempo que el run ya llevaba corriendo, para reanudar el cronómetro y el timeout. */
  elapsedOffsetMs?: number;
}

interface Options {
  /** Intervalo entre chequeos al backend (ms). Default 2000. */
  pollingIntervalMs?: number;
  /** Timeout máximo total (ms). Default 10min (interno, no se muestra). */
  timeoutMs?: number;
  /** Tick de refresco de elapsed (ms). Default 1000. */
  elapsedTickMs?: number;
  /** Tiempo que se mantiene la barra al 100% antes de limpiar. Default 2500. */
  completedHoldMs?: number;
  /** Callback con el progreso de una tabla en cada poll. */
  onProgresoUpdated?: (progreso: ProgresoSyncTablaMirror) => void;
  /** Callback con el estado fresco completo, tras cerrar el run contra Oracle. */
  onEstadoUpdated?: (estado: EstadoTablasMirrorResponse) => void;
  /** Callback al terminar (completed / timeout) tras el hold si aplica. */
  onSettled?: (tabla: string, status: ProgressStatus) => void;
}

const DEFAULT_POLLING = 2000;
const DEFAULT_TIMEOUT = 10 * 60 * 1000;
const DEFAULT_TICK = 1000;
const DEFAULT_COMPLETED_HOLD = 2500;

const findTabla = (
  estado: EstadoTablasMirrorResponse | any,
  tabla: string,
) => {
  const list: EstadoTablasMirrorResponse = Array.isArray(estado)
    ? estado
    : Array.isArray(estado?.data)
    ? estado.data
    : [];
  return list.find((t) => t.clave === tabla);
};

const getUpsertLabel = (phase?: SyncPhase | null): string => {
  switch (phase) {
    case "starting":
      return "Iniciando...";
    case "upserting":
      return "Aplicando cambios...";
    case "closing":
      return "Preparando cierre...";
    case "post_processing":
      return "Post-procesando...";
    case "completed":
      return "Completado";
    case "error":
      return "Error";
    default:
      return "Sincronizando...";
  }
};

/**
 * Trackea el progreso de la sincronización de una tabla mirror.
 *
 * El backend resuelve la fase del run comparando el marcador de ultima_sincronizacion:
 * mientras backend-sync trae los datos de Oracle la tabla conserva los registros viejos
 * (fase PREPARANDO, barra indeterminada), y recién tras el DELETE el conteo local
 * representa el avance real (fase DESCARGANDO).
 *
 * Al cerrar el run se consulta Oracle una única vez para que la tabla salga de INICIALIZANDO.
 */
export function useSyncProgress(options: Options = {}) {
  const {
    pollingIntervalMs = DEFAULT_POLLING,
    timeoutMs = DEFAULT_TIMEOUT,
    elapsedTickMs = DEFAULT_TICK,
    completedHoldMs = DEFAULT_COMPLETED_HOLD,
  } = options;

  const onProgresoUpdatedRef = useRef(options.onProgresoUpdated);
  const onEstadoUpdatedRef = useRef(options.onEstadoUpdated);
  const onSettledRef = useRef(options.onSettled);
  onProgresoUpdatedRef.current = options.onProgresoUpdated;
  onEstadoUpdatedRef.current = options.onEstadoUpdated;
  onSettledRef.current = options.onSettled;

  const [snapshots, setSnapshots] = useState<Record<string, SyncSnapshot>>({});
  const intervalsRef = useRef<Record<string, ReturnType<typeof setInterval>>>(
    {}
  );
  const elapsedIntervalsRef = useRef<
    Record<string, ReturnType<typeof setInterval>>
  >({});
  const holdTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {}
  );
  const stoppedRef = useRef<Record<string, boolean>>({});
  const inFlightRef = useRef<Record<string, boolean>>({});
  const runOptionsRef = useRef<Record<string, RunSyncOptions>>({});
  const observedActiveUpsertRef = useRef<Record<string, boolean>>({});

  const clearTimers = useCallback((tabla: string) => {
    const poll = intervalsRef.current[tabla];
    if (poll) {
      clearInterval(poll);
      delete intervalsRef.current[tabla];
    }
    const tick = elapsedIntervalsRef.current[tabla];
    if (tick) {
      clearInterval(tick);
      delete elapsedIntervalsRef.current[tabla];
    }
    const hold = holdTimeoutsRef.current[tabla];
    if (hold) {
      clearTimeout(hold);
      delete holdTimeoutsRef.current[tabla];
    }
  }, []);

  const clearSnapshot = useCallback(
    (tabla: string) => {
      clearTimers(tabla);
      delete stoppedRef.current[tabla];
      delete inFlightRef.current[tabla];
      delete runOptionsRef.current[tabla];
      delete observedActiveUpsertRef.current[tabla];
      setSnapshots((prev) => {
        if (!(tabla in prev)) return prev;
        const { [tabla]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [clearTimers]
  );

  /** Cierra el run: contrasta con Oracle para actualizar el estado y refresca el listado. */
  const cerrarRun = useCallback(async (tabla: string) => {
    try {
      await dashboardAPI.checkSyncCountTablaMirror(tabla);
    } catch (err) {
      console.log("useSyncProgress cierre error :>> ", err);
    }
    try {
      const estado = await dashboardAPI.estadoTablasMirror();
      onEstadoUpdatedRef.current?.(estado as EstadoTablasMirrorResponse);
    } catch (err) {
      console.log("useSyncProgress refresh estado error :>> ", err);
    }
  }, []);

  const stopPolling = useCallback(
    (tabla: string, finalStatus?: ProgressStatus) => {
      if (stoppedRef.current[tabla]) return;
      stoppedRef.current[tabla] = true;
      clearTimers(tabla);

      if (!finalStatus) {
        clearSnapshot(tabla);
        return;
      }

      setSnapshots((prev) => {
        const current = prev[tabla];
        if (!current) return prev;
        return {
          ...prev,
          [tabla]: {
            ...current,
            status: finalStatus,
            percent: finalStatus === "completed" ? 100 : current.percent,
          },
        };
      });

      void cerrarRun(tabla);

      if (finalStatus === "completed") {
        holdTimeoutsRef.current[tabla] = setTimeout(() => {
          clearSnapshot(tabla);
          onSettledRef.current?.(tabla, finalStatus);
        }, completedHoldMs);
      } else {
        onSettledRef.current?.(tabla, finalStatus);
      }
    },
    [cerrarRun, clearSnapshot, clearTimers, completedHoldMs]
  );

  const pollOnce = useCallback(
    async (tabla: string, startedAt: number, syncMode: SyncMode) => {
      if (stoppedRef.current[tabla] || inFlightRef.current[tabla]) return;
      inFlightRef.current[tabla] = true;
      try {
        if (syncMode === "replace") {
          const run = runOptionsRef.current[tabla];
          const progreso = (await dashboardAPI.progresoSyncTablaMirror(
            tabla,
            run?.marcadorSincronizacion,
          )) as ProgresoSyncTablaMirror;
          onProgresoUpdatedRef.current?.(progreso);

          if (stoppedRef.current[tabla]) return;

          setSnapshots((prev) => {
            const previous = prev[tabla];
            if (!previous || previous.status === "completed") return prev;
            return {
              ...prev,
              [tabla]: {
                ...previous,
                status:
                  progreso.fase === "PREPARANDO" ? "preparing" : "polling",
                percent: progreso.percent,
                cantidadRegistros: Number(progreso.cantidadRegistros ?? 0),
                cantidadRegistrosOracle: Number(
                  progreso.cantidadRegistrosOracle ?? 0,
                ),
                elapsedMs: Date.now() - startedAt,
              },
            };
          });

          if (progreso.completado) {
            stopPolling(tabla, "completed");
          }
          return;
        }

        // En upsert-hash el COUNT mirror no representa avance: se sigue la fase del run.
        const estado = await dashboardAPI.estadoTablasMirror();
        onEstadoUpdatedRef.current?.(estado as EstadoTablasMirrorResponse);

        const row = findTabla(estado, tabla);
        const cantidadRegistrosOracle = Number(
          row?.cantidadRegistrosOracle ?? 0,
        );
        const cantidadRegistros = Number(row?.syncRowsProcessed ?? 0);
        const phase = row?.syncPhase as SyncPhase | null | undefined;
        const batchEsDeEstaEjecucion =
          row?.syncMode === "upsert-hash" &&
          Number(row?.syncBatchId ?? 0) >= startedAt - 5000;
        if (
          batchEsDeEstaEjecucion ||
          phase === "starting" ||
          phase === "upserting" ||
          phase === "closing" ||
          phase === "post_processing"
        ) {
          observedActiveUpsertRef.current[tabla] = true;
        }
        const percent =
          phase === "completed"
            ? 100
            : phase === "post_processing"
              ? 95
              : phase === "closing"
                ? 92
                : Math.min(
                    90,
                    Math.round(
                      (cantidadRegistros /
                        Math.max(1, cantidadRegistrosOracle)) *
                        90,
                    ),
                  );
        const elapsed = Date.now() - startedAt;

        if (stoppedRef.current[tabla]) return;

        setSnapshots((prev) => {
          const previous = prev[tabla];
          if (!previous || previous.status === "completed") return prev;
          return {
            ...prev,
            [tabla]: {
              ...previous,
              status: phase && phase !== "starting" ? "polling" : "preparing",
              cantidadRegistros,
              cantidadRegistrosOracle,
              percent,
              elapsedMs: elapsed,
              syncPhase: phase,
              syncError: row?.syncError ?? null,
              label: getUpsertLabel(phase),
            },
          };
        });

        if (observedActiveUpsertRef.current[tabla] && phase === "completed") {
          stopPolling(tabla, "completed");
        } else if (
          observedActiveUpsertRef.current[tabla] &&
          phase === "error"
        ) {
          stopPolling(tabla, "error");
        }
      } catch (err) {
        console.log("useSyncProgress poll error :>> ", err);
      } finally {
        inFlightRef.current[tabla] = false;
      }
    },
    [stopPolling]
  );

  const startPolling = useCallback(
    (tabla: string, run: RunSyncOptions = {}) => {
      clearTimers(tabla);
      stoppedRef.current[tabla] = false;
      inFlightRef.current[tabla] = false;
      runOptionsRef.current[tabla] = run;
      delete observedActiveUpsertRef.current[tabla];
      const syncMode: SyncMode = run.usarUpsertHash ? "upsert-hash" : "replace";
      const elapsedOffsetMs = run.elapsedOffsetMs ?? 0;
      const startedAt = Date.now() - elapsedOffsetMs;

      setSnapshots((prev) => ({
        ...prev,
        [tabla]: {
          tabla,
          status: "preparing",
          percent: null,
          elapsedMs: elapsedOffsetMs,
          startedAt,
          cantidadRegistros: 0,
          cantidadRegistrosOracle: 0,
          syncMode,
          syncPhase: syncMode === "upsert-hash" ? "starting" : null,
          label: syncMode === "upsert-hash" ? "Iniciando..." : undefined,
        },
      }));

      // Pequeño delay para dar tiempo a que el sync en background arranque
      setTimeout(() => {
        if (!stoppedRef.current[tabla]) {
          pollOnce(tabla, startedAt, syncMode);
        }
      }, 500);

      intervalsRef.current[tabla] = setInterval(() => {
        pollOnce(tabla, startedAt, syncMode);
      }, pollingIntervalMs);

      elapsedIntervalsRef.current[tabla] = setInterval(() => {
        const elapsed = Date.now() - startedAt;
        setSnapshots((prev) => {
          const current = prev[tabla];
          if (!current || current.status === "completed") return prev;
          return { ...prev, [tabla]: { ...current, elapsedMs: elapsed } };
        });
        if (elapsed >= timeoutMs) {
          stopPolling(tabla, "timeout");
        }
      }, elapsedTickMs);
    },
    [
      clearTimers,
      elapsedTickMs,
      pollOnce,
      pollingIntervalMs,
      stopPolling,
      timeoutMs,
    ]
  );

  useEffect(() => {
    return () => {
      Object.keys(intervalsRef.current).forEach((tabla) => clearTimers(tabla));
      Object.keys(holdTimeoutsRef.current).forEach((tabla) => {
        clearTimeout(holdTimeoutsRef.current[tabla]);
      });
    };
  }, [clearTimers]);

  return {
    snapshots,
    startPolling,
    stopPolling,
    clearSnapshot,
  };
}
